/**
 * Tenants Hooks
 *
 * React Query hooks for fetching and managing tenant (institution) data.
 * Includes query for listing tenants and mutation for creating new tenants.
 *
 * Types derived from platform-admin.openapi.yaml via @aaimplatform/institutional-types.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantsApi } from '../api/client';
import type {
  Tenant,
  TenantListResponse,
  TenantDetail,
  TenantStatus,
  TenantType,
  CreateTenantRequest,
  UpdateTenantRequest,
} from '../api/client';

const QUERY_KEY = 'tenants';

/**
 * Hook for listing tenants with optional filtering.
 */
export function useTenants(params?: {
  search?: string;
  status?: TenantStatus;
  type?: TenantType;
}) {
  return useQuery<TenantListResponse, Error>({
    queryKey: [QUERY_KEY, params],
    queryFn: () => tenantsApi.list(params),
  });
}

/**
 * Hook for fetching a single tenant by ID.
 */
export function useTenant(tenantId: string) {
  return useQuery<TenantDetail, Error>({
    queryKey: [QUERY_KEY, tenantId],
    queryFn: () => tenantsApi.get(tenantId),
    enabled: !!tenantId,
  });
}

/**
 * Hook for creating (onboarding) a new tenant.
 * Invalidates the tenant list on success so the grid refreshes.
 */
export function useCreateTenant() {
  const queryClient = useQueryClient();

  return useMutation<TenantDetail, Error, CreateTenantRequest>({
    mutationFn: (body: CreateTenantRequest) => tenantsApi.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Hook for editing a tenant's organization profile (GH #4155: the operator's
 * primary contact edit on TenantDetail). Invalidates every tenant query on
 * success, so the detail page and the list both re-read the saved value.
 */
export function useUpdateTenant(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation<Tenant, Error, UpdateTenantRequest>({
    mutationFn: (body: UpdateTenantRequest) => tenantsApi.update(tenantId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useDeleteTenant() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (tenantId: string) => tenantsApi.delete(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
